import { useState } from "react";
import axios from "axios";
import { API } from "./api";
import { Mail, Lock, Shield, AlertCircle } from "lucide-react";

const Field = ({ label, icon: Icon, ...props }) => (
  <label className="block">
    <span className="block text-[11px] font-bold tracking-[0.14em] uppercase text-slate-500 mb-1.5">{label}</span>
    <div className="relative">
      {Icon && <Icon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />}
      <input
        {...props}
        className={`w-full bg-white border border-slate-300 rounded-lg py-2.5 ${Icon ? "pl-10" : "pl-3"} pr-3 text-sm focus:border-slate-700 focus:outline-none`}
      />
    </div>
  </label>
);

export default function LoginScreen({ onLoggedIn }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [needsOtp, setNeedsOtp] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password, otp: otp || undefined });
      onLoggedIn(data.user, data.token);
    } catch (err) {
      const detail = err.response?.data?.detail || "Could not sign in";
      if (typeof detail === "string" && detail.toLowerCase().includes("mfa code")) {
        setNeedsOtp(true);
        setError("Enter the 6-digit code from your authenticator app.");
      } else {
        setError(typeof detail === "string" ? detail : "Could not sign in");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md bg-white rounded-xl shadow-xl p-8 border border-slate-200">
        <div className="flex items-center gap-3 mb-6">
          <img src="/logo.png" alt="Elin's Agency" className="h-10 w-auto" />
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase text-slate-500">Elin's Agency</p>
            <h1 className="font-serif text-xl text-slate-800">Workspace sign-in</h1>
          </div>
        </div>
        <p className="text-[12px] text-slate-500 mb-6 leading-relaxed">
          One sign-in for the three workspaces (staff, broker and customer). You'll be routed to the right place after login.
        </p>
        {error && (
          <div data-testid="agency-login-error" className="mb-4 bg-rose-50 border border-rose-200 text-rose-700 text-[13px] px-3 py-2 rounded flex gap-2 items-start">
            <AlertCircle size={14} className="mt-0.5 shrink-0" /> <span>{error}</span>
          </div>
        )}
        <form onSubmit={submit} className="space-y-4">
          <Field label="Email" type="email" required icon={Mail} value={email} onChange={(e) => setEmail(e.target.value)} data-testid="agency-email" placeholder="you@firm.co.uk" />
          <Field label="Password" type="password" required icon={Lock} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="agency-password" placeholder="••••••••" />
          {needsOtp && (
            <Field label="MFA Code (6 digits)" type="text" icon={Shield} value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} inputMode="numeric" maxLength={6} data-testid="agency-otp" placeholder="123456" />
          )}
          <button
            type="submit"
            disabled={busy}
            data-testid="agency-login-submit"
            className="w-full bg-slate-800 hover:bg-slate-900 text-white font-semibold py-2.5 rounded-lg disabled:opacity-60 transition-colors"
          >
            {busy ? "Signing in…" : "Sign in"}
          </button>
        </form>

        <div className="mt-8 pt-6 border-t border-slate-200">
          <p className="text-[11px] font-bold tracking-[0.14em] uppercase text-slate-500 mb-2">Demo credentials</p>
          <ul className="text-[12px] text-slate-600 space-y-1 font-mono">
            <li>admin@elinsagency.co.uk</li>
            <li>manager@elinsagency.co.uk</li>
            <li>staff@elinsagency.co.uk</li>
            <li>broker@jmw-mortgages.co.uk</li>
            <li>marcus@customer.example.com</li>
            <li className="text-slate-500 pt-1">Password: AgencyDemo2026!</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
